// src/pages/Comanda.js
import { formatarVariacoes } from '../utils/VariacaoUtils';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Form/Header';
import MainContent from '../components/Form/MainContent';
import { useApi } from '../hooks/useApi';
import { useTheme } from '../contexts/ThemeContext';
import { formatCurrency } from '../utils/numberUtils';
import { formatDate, formatTime } from '../utils/dateTimeUtils';
import { getComandaPedidoNumero } from '../utils/pedidoUtils';
import './Comanda.css';

const statusConfig = {
  0: { label: 'Aberta', className: 'open' },
  1: { label: 'Fechada', className: 'closed' },
  2: { label: 'Cancelada', className: 'cancelled' },
};

const getComandaStatus = (status) => {
  return statusConfig[status] || { label: 'Status desconhecido', className: 'unknown' };
};

const getPedidoStatus = (status) => {
  return status || 'Sem status';
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  return `${formatDate(value)} ${formatTime(value)}`;
};

const hasItemDetails = (item) => {
  return Boolean(
    formatarVariacoes(item) ||
    item?.apresentacao ||
    (item?.observacao && item.observacao.trim()) ||
    item?.pedidoItemAdicionais?.length ||
    item?.pedidoItemConjuntos?.length
  );
};

const Comanda = () => {
  const { chave } = useParams();
  const { fetchData } = useApi();
  const { corApp, configModulo } = useTheme();
  const [comanda, setComanda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadComanda = async () => {
      setLoading(true);
      setError('');

      try {
        const result = await fetchData('/Comanda/BuscaComandaParaCliente', { chave });
        setComanda(result);
      } catch (err) {
        setComanda(null);
        setError('Nao foi possivel carregar esta comanda.');
      } finally {
        setLoading(false);
      }
    };

    if (chave) {
      loadComanda();
    } else {
      setError('Chave da comanda nao informada.');
      setLoading(false);
    }
  }, [chave]);

  const status = getComandaStatus(comanda?.status);
  const itens = comanda?.comandaItens || [];

  return (
    <>
      <Header semBotao={true} titulo="Comanda" />
      <MainContent>
        <div className="comanda-page">
          <section className="comanda-hero">
            <div className={`comanda-status-badge ${status.className}`}>
              <span className="comanda-status-dot" />
              {status.label}
            </div>
          </section>

          {loading && (
            <div className="comanda-feedback-card">
              <p>Carregando dados da comanda...</p>
            </div>
          )}

          {!loading && error && (
            <div className="comanda-feedback-card error">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && comanda && (
            <>
              <section className="comanda-card">
                <div className="comanda-top-row">
                  <div className="comanda-info-item">
                    <span className="label">Mesa</span>
                    <strong>{comanda.mesa?.codigo || '-'}</strong>
                  </div>
                  <div className="comanda-info-item">
                    <span className="label">Comanda</span>
                    <strong>{comanda.codigo || '-'}</strong>
                  </div>
                </div>
              </section>

              <section className="comanda-card">
                <div className="comanda-card-header">
                  <h3>Itens consumidos</h3>
                </div>

                {itens.length > 0 ? (
                  <div className="comanda-items-list">
                    {itens.map((item) => (
                      <article key={item.id} className="comanda-item-card">
                        <div className="comanda-item-main">
                          <div>
                            <p className="comanda-item-title">
                              {item.item}. {item.produto?.descricao || 'Item sem descricao'}
                            </p>
                            <p className="comanda-item-meta">
                              Pedido #{getComandaPedidoNumero(item, configModulo?.UsaIdDia) || '-'} - {getPedidoStatus(item.statusPedido)}
                            </p>
                            {item.dataCad && (
                              <p className="comanda-item-meta">
                                Lancado em {formatDateTime(item.dataCad)}
                              </p>
                            )}
                          </div>
                          <div className="comanda-item-values">
                            <strong>{formatCurrency(item.valorTotal || 0)}</strong>
                            <span>{item.qtde || 0}x {formatCurrency(item.valorUnitario || 0)}</span>
                          </div>
                        </div>

                        {hasItemDetails(item) && (
                          <div className="comanda-item-details">
                            {formatarVariacoes(item) && (
                              <p className="comanda-item-detail-line">
                                <span className="detail-label">Variacao:</span> {formatarVariacoes(item)}
                              </p>
                            )}

                            {item.apresentacao && (
                              <p className="comanda-item-detail-line">
                                <span className="detail-label">Apresentacao:</span> {item.apresentacao}
                              </p>
                            )}

                            {item.observacao && item.observacao.trim() && (
                              <p className="comanda-item-detail-line">
                                <span className="detail-label">Observacao:</span> {item.observacao}
                              </p>
                            )}

                            {item.pedidoItemAdicionais?.length > 0 && (
                              <div className="comanda-item-detail-group">
                                <span className="detail-label">Adicionais</span>
                                {item.pedidoItemAdicionais.map((adicional) => (
                                  <p key={adicional.id} className="comanda-item-detail-line">
                                    {adicional.adicional?.descricao || 'Adicional'}
                                    {adicional.valor ? ` (${formatCurrency(adicional.valor)})` : ''}
                                  </p>
                                ))}
                              </div>
                            )}

                            {item.pedidoItemConjuntos?.length > 0 && (
                              <div className="comanda-item-detail-group">
                                <span className="detail-label">Conjuntos</span>
                                {item.pedidoItemConjuntos.map((conjunto) => (
                                  <p key={conjunto.id} className="comanda-item-detail-line">
                                    {conjunto.nomeConjunto ? `${conjunto.nomeConjunto}: ` : ''}
                                    {conjunto.descricao || 'Item do conjunto'}
                                    {conjunto.qtdItens ? ` x${conjunto.qtdItens}` : ''}
                                    {conjunto.valorAdicional ? ` (${formatCurrency(conjunto.valorAdicional)})` : ''}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="comanda-feedback-card">
                    <p>Esta comanda ainda nao possui itens lancados.</p>
                  </div>
                )}
              </section>

              <section className="comanda-total-card" style={{ borderColor: corApp }}>
                <div>
                  <span className="label">Total da comanda</span>
                  <strong>{formatCurrency(comanda.valorTotal || 0)}</strong>
                </div>
                <div className="comanda-total-secondary">
                  <span>Pago: {formatCurrency(comanda.valorPago || 0)}</span>
                  <span>Em aberto: {formatCurrency(comanda.valorRestante || 0)}</span>
                </div>
              </section>
            </>
          )}
        </div>
      </MainContent>
      
    </>
  );
};

export default Comanda;
